import HeroFX from "./HeroFX";
import React, { useState, useEffect } from "react";
import { fetchHero } from "../api";
import { setPageDescription } from "../seo";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";
import PageBody from "./PageBody";
import ContactForm from "./ContactForm";

const defaultContent = {
  page_title: "",
  tagline: "Empowering Businesses Digitally",
  description: "Web Development | Mobile Applications | Desktop Software | Digital Growth Solutions",
  icons: "",
  image: null,
  body_content: "",
  body_image: null,
  body_button_text: "",
};

// The generic hero for standalone pages (About, Contact, Co-Work, Foundation, Portfolio - any
// page that isn't a Service/AI Tools category). Mirrors ServiceDetailContent's compact banner
// exactly: image box, typed title, description, tech-icon pills - same layout/spacing as the
// Service Detail pages, for one consistent hero design across the whole site.
export default function HeroSection({ slug = "home", fallbackTitle = "Home" }) {
  const [content, setContent] = useState(defaultContent);
  const [typedTitle, setTypedTitle] = useState("");

  useEffect(() => {
    fetchHero(slug).then(setContent).catch(() => {});
  }, [slug]);

  const pageTitle = content.page_title || fallbackTitle;

  // Typewriter effect: reveal the title one character at a time, matching ServiceDetailContent.
  useEffect(() => {
    const text = content.tagline || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [content.tagline]);

  useEffect(() => {
    document.title = `${pageTitle} - ${content.tagline}`;
    setPageDescription(content.description);
  }, [pageTitle, content.tagline, content.description]);

  const icons = (content.icons || "").split(",").filter(Boolean);
  // About's own image is shown full width/height of its column (matching the text column's
  // height) rather than the compact fixed-height box every other generic page uses.
  const isAbout = slug === "about";

  return (
    <>
    <section id={slug} className="relative py-10 scroll-mt-20 text-white border-b border-navy-border bg-navy overflow-hidden">
      <HeroFX />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className={`grid md:grid-cols-[30%_70%] gap-8 ${isAbout ? "items-start" : "items-center"}`}>
          <div className="rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
            {content.image ? (
              <img
                src={content.image}
                alt={pageTitle}
                className="w-full aspect-square object-cover bg-navy"
              />
            ) : (
              <div className="w-full aspect-square bg-brand-gradient" />
            )}
          </div>

          <div>
            <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight">
              {typedTitle}
              <span className="typing-cursor inline-block w-[3px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
            </h1>
            {content.description && (
              <p className="mt-3 text-gray-300">{content.description}</p>
            )}

            {icons.length > 0 && (
              <div className="mt-5">
                <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">
                  Technologies We Use
                </p>
                <div className="flex flex-wrap gap-3">
                  {icons.map((key) => {
                    const entry = DEV_TOOL_ICON_MAP[key];
                    if (!entry) return null;
                    const { Icon, label } = entry;
                    return (
                      <span
                        key={key}
                        title={label}
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm"
                      >
                        <Icon size={16} />
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>

    <PageBody content={content} pageTitle={pageTitle} />
    {slug === "contact" && <ContactForm />}
    </>
  );
}
